import {
  CheckCircle2,
  CreditCard,
  FileText,
  Clock,
  ShieldCheck,
  ArrowRight,
  PartyPopper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const NEXT_STEPS = [
  {
    icon: Clock,
    title: "Our team reviews your request",
    description:
      "A real human will confirm the AI-generated scope before any work begins.",
    accent: "bg-primary/10 text-primary",
  },
  {
    icon: CreditCard,
    title: "Small & medium tasks — we get started",
    description:
      "If you authorised a payment hold, no charge is taken until the work is completed and reviewed.",
    accent: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  },
  {
    icon: FileText,
    title: "Larger jobs — we'll send a quote first",
    description:
      "For bigger projects, we'll prepare a detailed proposal. Work only begins after you approve it.",
    accent: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  },
  {
    icon: ShieldCheck,
    title: "Maintenance plan clients",
    description:
      "If your request is covered by your plan, we'll start right away — no payment needed.",
    accent: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  },
];

export function SuccessMessage() {
  return (
    <div className="py-12 max-w-xl mx-auto animate-in fade-in slide-in-from-bottom-6 duration-700">
      {/* Hero checkmark */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/10 mb-5">
          <div className="relative">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 dark:text-emerald-500 animate-in zoom-in duration-500" />
            <PartyPopper className="absolute -top-2 -right-3 w-5 h-5 text-amber-500 animate-in fade-in zoom-in duration-700 delay-300" />
          </div>
        </div>

        <h1 className="text-3xl font-bold mb-3 font-heading">
          You're all set!
        </h1>
        <p className="text-lg text-muted-foreground">
          We've received your request and our team will begin reviewing it
          shortly. You'll get an email confirmation any moment now.
        </p>
      </div>

      {/* What happens next */}
      <Card className="border-border overflow-hidden mb-8">
        <div className="h-1 bg-gradient-to-r from-emerald-500/60 to-primary/30" />
        <CardContent className="pt-6 pb-6">
          <h3 className="font-semibold mb-5 font-heading text-sm uppercase tracking-wide text-muted-foreground">
            What happens next
          </h3>
          <div className="space-y-5">
            {NEXT_STEPS.map((step, index) => (
              <div
                key={step.title}
                className="flex items-start gap-3 animate-in fade-in slide-in-from-left-3 duration-400"
                style={{ animationDelay: `${200 + index * 100}ms`, animationFillMode: "backwards" }}
              >
                <div
                  className={`flex items-center justify-center w-9 h-9 rounded-lg ${step.accent} shrink-0 mt-0.5`}
                >
                  <step.icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{step.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground mt-5 pt-4 border-t border-border">
            Standard turnaround for small tasks is 2–3 working days. You'll
            receive email updates as we progress.
          </p>
        </CardContent>
      </Card>

      {/* Action */}
      <div className="text-center">
        <Button
          size="lg"
          onClick={() => window.location.reload()}
          className="active-elevate-2"
        >
          Submit Another Request
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
